import _ from 'lodash';
import React from 'react';
import { ErrorBoundary, useErrorHandler } from 'react-error-boundary';
import { useLocation, useHistory, Redirect, Link } from 'react-router-dom';
import { useEffectOnce } from 'react-use';
import { useCreatePartnerRecords } from './createDatabaseRecords';
import { useNavigateToPartnerOAuthInitiate } from './PartnerHooks';
import { KA_QUEUE } from '../IntegrationConstants';
import LoadingIndicator from '../../accountSettings/common/LoadingIndicator';
import {
  LOCATION_KEY_DATASOURCES,
  DataSourceDisplayName,
  RedirectBehavior,
} from '../../dataSources/dataSourceConstants';
import { ProfitRoverPrimaryButton, ProfitRoverSecondaryButton } from '../../forms/ProfitRoverButtons';
import GenericErrorBoundaryPage, {
  Message,
  MessageBubble,
  Title,
} from '../../generic/errorBoundary/GenericErrorBoundaryPage';
import HeaderAwarePage from '../../generic/HeaderAwarePage';
import Header from '../../header/header';
import { useRequestDataSync, useUpdateDatasetConfig } from '../../../data-access/mutation/datasetConfigs';
import { useUpdateGuidedSetup } from '../../../data-access/mutation/guidedSetup';
import { useCreatePartnerSchedule } from '../../../data-access/mutation/schedules';
import { useObtainPartnerAccessToken } from '../../../data-access/mutation/oauthPartner';
import { useDatasets } from '../../../data-access/query/datasets';
import { useDatasetConfigs } from '../../../data-access/query/datasetConfigs';
import { useGuidedSetup } from '../../../data-access/query/guidedSetup';
import { useSchedules } from '../../../data-access/query/schedules';

/**
 * This collection of functions and components is used for integration partners that follow an Oauth flow or a
 * simple post processing of a location based authorization.
 *
 * Oauth partners:
 *
 * Square: https://developer.squareup.com/docs/oauth-api/walkthrough
 * GoTab: https://docs.gotab.io/docs/authorization-code-grant-flow
 * Heartland Retail: https://dev.retail.heartland.us/#oauth
 * Lightspeed K: https://api-portal.lsk.lightspeed.app/quick-start/authentication/authorization-overview
 *
 * Location authorization partners:
 *
 * Heartland Restaurant: https://api.hrpos.heartland.us
 */

const LoadingMessage = ({ message }) => (
  <HeaderAwarePage scrollable={false}>
    <Header />
    <div
      className="container d-flex flex-column align-items-center justify-content-center text-center"
      style={{ height: '100%' }}
    >
      <h4 className="mb-5">{message}</h4>
      <LoadingIndicator />
    </div>
  </HeaderAwarePage>
);

const FINALIZATION_MESSAGE = 'Please wait a moment while we finalize the integration with your account.';

class AuthorizationError extends Error {}
class InvalidRequestError extends Error {}
class MalformedUrlError extends Error {}
class CompletionError extends Error {}

const usePartnerRedirectUrlParams = partner => {
  const location = useLocation();
  const { search } = location;

  const urlParams = new URLSearchParams(search);

  const csrfFromPartnerOAuth = urlParams.get('state');
  const localStoragePartnerState = localStorage.getItem(`${_.toLower(partner)}_state`);

  // Verify token to help mitigate csrf attacks
  if (csrfFromPartnerOAuth && csrfFromPartnerOAuth !== localStoragePartnerState) {
    throw new InvalidRequestError('Invalid authorization request');
  }

  return urlParams;
};

const PartnerAuthParseRedirectUrl = ({ partner, onFinish }) => {
  const urlParams = usePartnerRedirectUrlParams(partner);

  const responseType = urlParams.get('response_type');
  const authCode = urlParams.get('code');
  const error = urlParams.get('error');
  const state = urlParams.get('state');

  const obtainPartnerAccessToken = useObtainPartnerAccessToken(partner, { useErrorBoundary: true });
  const handleError = useErrorHandler();

  if (error) {
    const errorDescription = urlParams.get('error_description');

    if (error === 'access_denied' && errorDescription === 'user_denied') {
      throw new AuthorizationError('Authorization failed: Access Denied');
    } else {
      throw new AuthorizationError(`Authorization failed - Error: ${error}, Details=${errorDescription}`);
    }
  }

  if (authCode == null) {
    throw new MalformedUrlError('No authorization code was provided');
  } else if (responseType && responseType !== 'code') {
    throw new MalformedUrlError('Unrecognized response type');
  }

  const consumeAuthCodeToCreateCredentials = async () => {
    await obtainPartnerAccessToken.mutateAsync({ authCode, state });

    try {
      await onFinish();
    } catch {
      handleError(new CompletionError('Failed to finish setup after redeeming auth code'));
    }
  };

  // Permission was granted by the user, so now we must...
  useEffectOnce(() => {
    consumeAuthCodeToCreateCredentials();
  });

  return <LoadingMessage message={FINALIZATION_MESSAGE} />;
};

const PartnerLocationAuth = ({ partner, onFinish }) => {
  const handleError = useErrorHandler();

  const complete = async () => {
    try {
      await onFinish();
    } catch {
      handleError(new CompletionError(`Could not complete setup for ${DataSourceDisplayName[partner]}`));
    }
  };

  useEffectOnce(() => {
    complete();
  });

  return <LoadingMessage message={FINALIZATION_MESSAGE} />;
};

const createSchedules = async (
  partner,
  customerName,
  partnerDatasetConfigs,
  datasets,
  processSchedules,
  createPartnerSchedule,
) => {
  const createdSchedulePromises = [];
  partnerDatasetConfigs.forEach(config => {
    const dataset = datasets.find(e => e.dataset_config_id === config.dataset_config_id);
    const schedule = processSchedules.find(e => e.message.dataset_ids === dataset.dataset_id);

    if (!schedule) {
      const { dataset_id: datasetId } = dataset;
      const { industry, data_type: dataType } = config;
      createdSchedulePromises.push(
        createPartnerSchedule.mutateAsync({
          datasetId,
          industry,
          dataType,
          description: `${DataSourceDisplayName[partner]} integration for ${customerName}`,
        }),
      );
    }
  });

  await Promise.allSettled(createdSchedulePromises);
};

const updateDatasetConfigs = async (datasetConfigs, updateDatasetConfig) => {
  const updateDatasetConfigPromises = [];
  datasetConfigs.forEach(config => {
    const { dataset_config_id: datasetConfigId } = config;

    // Sends a PROCESS message as a side-effect
    updateDatasetConfigPromises.push(
      updateDatasetConfig.mutateAsync({
        datasetConfigId,
        datasetConfig: { is_editable: false },
      }),
    );
  });

  await Promise.allSettled(updateDatasetConfigPromises);
};

const useCompletionBehavior = (partner, redirectBehavior) => {
  const customerName = localStorage.getItem('customer_name');

  const { data: schedules = [], isFetching: isFetchingSchedules } = useSchedules();
  const { data: datasets = [], isFetching: isFetchingDatasets } = useDatasets();
  const { data: datasetConfigs = [], isFetching: isFetchingDatasetConfigs } = useDatasetConfigs();

  const isLoading = isFetchingSchedules || isFetchingDatasets || isFetchingDatasetConfigs;

  const history = useHistory();
  const createPartnerSchedule = useCreatePartnerSchedule();
  const updateDatasetConfig = useUpdateDatasetConfig();

  const requestDataSyncMutation = useRequestDataSync();
  const partnerDatasetConfigs = datasetConfigs.filter(e => e.partner === partner);

  const requestDataSync = async () => {
    const dataSyncPromises = [];
    partnerDatasetConfigs.forEach(config => {
      const { dataset_config_id: datasetConfigId } = config;

      dataSyncPromises.push(requestDataSyncMutation.mutateAsync({ datasetConfigId }));
    });

    await Promise.allSettled(dataSyncPromises);
  };

  const processSchedules = schedules.filter(e => e.ka_queue === KA_QUEUE.PROCESS);

  const onFinish = async () => {
    await createSchedules(
      partner,
      customerName,
      partnerDatasetConfigs,
      datasets,
      processSchedules,
      createPartnerSchedule,
    );

    if (redirectBehavior !== RedirectBehavior.RECONNECT) {
      await updateDatasetConfigs(datasetConfigs, updateDatasetConfig);
    } else {
      await requestDataSync();
    }

    history.push('/data-sources');
  };

  return { isLoading, onFinish };
};

/**
 * This Component serves double-time for the ADD_NEW_DATA_SOURCE and RECONNECT flows since they are
 * _almost_ identical in behavior.
 */
const AddNewDataSourceRedirect = ({ partner, redirectBehavior }) => {
  const { isLoading, onFinish } = useCompletionBehavior(partner, redirectBehavior);

  if (isLoading) {
    return <LoadingMessage message="Verifying app permissions..." />;
  }

  if (LOCATION_KEY_DATASOURCES.includes(partner)) {
    return <PartnerLocationAuth partner={partner} onFinish={onFinish} />;
  }

  return <PartnerAuthParseRedirectUrl partner={partner} onFinish={onFinish} />;
};

const GuidedSetupRedirect = ({ partner }) => {
  const customerName = localStorage.getItem('customer_name');
  const { data: guidedSetup = {}, isLoading: isFetchingGuidedSetup } = useGuidedSetup();
  const {
    naics_industry: { naics_code: naicsCode, ka_code: kaCode } = {},
    data_source: { item_classes: itemClasses } = {},
  } = guidedSetup;

  const { data: schedules = [], isLoading: isFetchingSchedules } = useSchedules();
  const { refetch: refetchDatasets } = useDatasets();
  const { refetch: refetchDatasetConfigs } = useDatasetConfigs();

  const isLoading = isFetchingGuidedSetup || isFetchingSchedules;

  const { isInitializing, createPartnerRecords } = useCreatePartnerRecords();
  const updateDatasetConfig = useUpdateDatasetConfig();
  const createPartnerSchedule = useCreatePartnerSchedule();
  const updateGuidedSetup = useUpdateGuidedSetup();
  const history = useHistory();

  const onFinish = async () => {
    await createPartnerRecords({ partner, itemClasses, naicsCode, kaCode });

    const { data: datasets } = await refetchDatasets();
    const { data: datasetConfigs } = await refetchDatasetConfigs();
    const partnerDatasetConfigs = datasetConfigs.filter(e => e.partner === partner);
    const processSchedules = schedules.filter(e => e.ka_queue === KA_QUEUE.PROCESS);

    await createSchedules(
      partner,
      customerName,
      partnerDatasetConfigs,
      datasets,
      processSchedules,
      createPartnerSchedule,
    );
    await updateDatasetConfigs(datasetConfigs, updateDatasetConfig);

    await updateGuidedSetup.mutateAsync({
      is_complete: true,
    });

    history.push('/welcome', { showCompletedGuidedSetupModal: true });
  };

  if (isLoading || isInitializing) {
    return <LoadingMessage message="Please wait a moment..." />;
  }

  if (LOCATION_KEY_DATASOURCES.includes(partner)) {
    return <PartnerLocationAuth partner={partner} onFinish={onFinish} />;
  }

  return <PartnerAuthParseRedirectUrl partner={partner} onFinish={onFinish} />;
};

const PERMISSIONS_GRANT_DENIED_ERROR_MESSAGE = partner =>
  `We require these permissions for integration with your ${DataSourceDisplayName[partner]} account. 
  To proceed, you must grant permission for us to access your data when prompted.`;

const REQUEST_CANNOT_BE_COMPLETED_MESSAGE = (
  <>
    This request cannot be completed at this time. Contact our <Link to="/help">support team</Link>.
  </>
);
const URL_MALFORMED_SUPPORT_REQUEST_ERROR_MESSAGE = (
  <>
    This URL is invalid. Contact our <Link to="/help">support team</Link>.
  </>
);
const DEFAULT_GENERIC_MESSAGE = (
  <>
    An unknown error occurred. Contact our <Link to="/help">support team</Link>.
  </>
);

export function useNavigateToPartnerInstructions(partner, redirectBehavior) {
  const history = useHistory();

  const MAP_REDIRECT_BEHAVIOR_TO_HISTORY_ENTRY = {
    [RedirectBehavior.ADD_NEW_DATA_SOURCE]: `/data-sources/add-new/${_.toLower(partner)}`,
    [RedirectBehavior.GUIDED_SETUP]: {
      pathname: `/guided-setup/partner`,
      state: { partner },
    },
    [RedirectBehavior.RECONNECT]: `/data-sources/reconnect/${_.toLower(partner)}`,
  };

  const nextHistoryEntry = MAP_REDIRECT_BEHAVIOR_TO_HISTORY_ENTRY[redirectBehavior];

  return () => {
    if (nextHistoryEntry) {
      history.push(nextHistoryEntry);
    }
  };
}

const ErrorFallback = ({ error, resetErrorBoundary, partner, redirectBehavior }) => {
  const initiateOauth = useNavigateToPartnerOAuthInitiate(partner);
  const navigateBack = useNavigateToPartnerInstructions(partner, redirectBehavior);

  const title = `${DataSourceDisplayName[partner]} Integration Setup Failure`;
  let friendlyErrMessage = DEFAULT_GENERIC_MESSAGE;
  let buttons = (
    <>
      <ProfitRoverPrimaryButton onClick={initiateOauth}>Try Again</ProfitRoverPrimaryButton>
      <ProfitRoverSecondaryButton onClick={navigateBack}>Go Back</ProfitRoverSecondaryButton>
    </>
  );

  if (error instanceof AuthorizationError) {
    friendlyErrMessage = PERMISSIONS_GRANT_DENIED_ERROR_MESSAGE(partner);
  } else if (error instanceof InvalidRequestError) {
    friendlyErrMessage = REQUEST_CANNOT_BE_COMPLETED_MESSAGE;
  } else if (error instanceof MalformedUrlError) {
    friendlyErrMessage = URL_MALFORMED_SUPPORT_REQUEST_ERROR_MESSAGE;
    buttons = (
      <>
        <ProfitRoverPrimaryButton onClick={resetErrorBoundary}>Retry</ProfitRoverPrimaryButton>
        <ProfitRoverSecondaryButton onClick={navigateBack}>Go Back</ProfitRoverSecondaryButton>
      </>
    );
  }

  // TODO: Remove this entry from the history stack?

  return (
    <GenericErrorBoundaryPage showingHeader={false}>
      <MessageBubble>
        <div className="h-100 d-flex flex-column justify-content-between">
          <div className="mb-5">
            <Title>{title}</Title>
            <Message>{friendlyErrMessage}</Message>
          </div>
          <div className="d-flex">{buttons}</div>
        </div>
      </MessageBubble>
    </GenericErrorBoundaryPage>
  );
};

const PartnerAuthRedirect = ({ partner }) => {
  const redirectBehavior = localStorage.getItem(`${_.toLower(partner)}_redirect_behavior`);

  let component = null;
  if (redirectBehavior === RedirectBehavior.GUIDED_SETUP) {
    component = (
      <ErrorBoundary
        fallbackRender={props => <ErrorFallback {...props} partner={partner} redirectBehavior={redirectBehavior} />}
      >
        <GuidedSetupRedirect partner={partner} />
      </ErrorBoundary>
    );
  }

  if ([RedirectBehavior.ADD_NEW_DATA_SOURCE, RedirectBehavior.RECONNECT].includes(redirectBehavior)) {
    component = (
      <ErrorBoundary
        fallbackRender={props => <ErrorFallback {...props} partner={partner} redirectBehavior={redirectBehavior} />}
      >
        <AddNewDataSourceRedirect partner={partner} redirectBehavior={redirectBehavior} />
      </ErrorBoundary>
    );
  }

  return component ?? <Redirect to="/login-redirect" />;
};

export default PartnerAuthRedirect;
